import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Paper,
  Collapse,
  Stack, 
  Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import AddTaskIcon from '@mui/icons-material/AddTask';
import VisibilityIcon from '@mui/icons-material/Visibility';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import { formatCurrency } from '../../../helper/currencyFormatter.helper';
import { ROLES } from '../../../constants/general.constant';
import { Deal, Customer } from '../../../validation/customer.validation';
import BankerDocumentsView from './BankerDocumentsView';
import { getStatusColor } from './dealsStatusColor';
import { dealsStatus } from '../../../constants/general.constant';

type Column = {
  id: keyof Deal | 'actions' | 'assignedBankers' | 'expand';
  label: string;
  sortable: boolean;
};

const columns: Array<Column> = [
  { id: 'expand', label: '', sortable: false },
  { id: 'loanName', label: 'Loan Name', sortable: true },
  { id: 'customer', label: 'Customer', sortable: true },
  { id: 'honor', label: 'Honor', sortable: true },
  { id: 'loanSize', label: 'Loan Size', sortable: true },
  { id: 'assignedBankers', label: 'Assigned Bankers', sortable: false },
  { id: 'maxLTV', label: 'Max LTV', sortable: true },
  { id: 'minDSCRatio', label: 'Min DSCR', sortable: true },
  { id: 'status', label: 'Status', sortable: true },
  { id: 'closingDate', label: 'Closing', sortable: true },
  { id: 'actions', label: 'Actions', sortable: false },
];

type Props = {
  deals: Deal[];
  loggedInUser: Customer | null;

  orderBy: keyof Deal;
  order: 'asc' | 'desc';
  onSort: (columnId: keyof Deal) => void;

  expandedRows: Set<string>;
  onToggleExpand: (dealId: string) => void;

  onAssignClick: (deal: Deal) => void;
  onAddTaskClick?: (deal: Deal) => void;
  onEditClick: (deal: Deal) => void;
  onDeleteClick: (dealId?: string) => void;

  onViewTermSheet: (deal: Deal, bankerId: string) => void;
  onClosingClick: (deal: Deal) => void;
  actionsDisabled?: boolean;
};

const getCustomerName = (deal: Deal) => {
  const customer: any = deal.customer;
  if (!customer) return '-';
  return `${customer.firstName || ''} ${customer.middleName || ''} ${customer.lastName || ''}`.trim() || customer.name || '-';
};

const formatClosingDate = (date?: string | null) => {
  if (!date) return 'Add Date';
  const [year, month, day] = date.slice(0, 10).split('-');
  return year && month && day ? `${month}/${day}/${year}` : date;
};

const DealsDesktopTableView: React.FC<Props> = ({
  deals,
  loggedInUser,
  orderBy,
  order,
  onSort,
  expandedRows,
  onToggleExpand,
  onAssignClick,
  onAddTaskClick = () => undefined,
  onEditClick,
  onDeleteClick,
  onViewTermSheet,
  onClosingClick,
  actionsDisabled = false,
}) => {
  return (
    <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
      <Table size="medium">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f9fafb' }}>
            {columns.map((col) => {
              const isActive = orderBy === col.id && col.sortable;
              return (
                <TableCell
                  key={col.id}
                  sx={{ fontWeight: 'bold', border: '1px solid #e5e7eb', color: '#1f2937' }}
                  align={col.id === 'actions' ? 'right' : 'left'}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={isActive}
                      direction={isActive ? order : 'asc'}
                      onClick={() => onSort(col.id as keyof Deal)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {deals.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                sx={{ border: '1px solid #e5e7eb', textAlign: 'center', py: 6, color: '#6b7280' }}
              >
                No deals found
              </TableCell>
            </TableRow>
          )}

          {deals.map((d) => (
            <React.Fragment key={d.id}>
              <TableRow hover sx={{ '&:last-child td, &:last-child th': { borderBottom: 0 } }}>
                <TableCell sx={{ border: '1px solid #e5e7eb' }}>
                  {d?.assignedBanks && d.assignedBanks.length > 0 && (
                    <IconButton size="small" onClick={() => onToggleExpand(d.id!)}>
                      {expandedRows.has(d.id!) ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                    </IconButton>
                  )}
                </TableCell>

                <TableCell sx={{ border: '1px solid #e5e7eb' }}>{d.loanName}</TableCell>
                <TableCell sx={{ border: '1px solid #e5e7eb' }}>{getCustomerName(d)}</TableCell>

                <TableCell sx={{ border: '1px solid #e5e7eb' }}>
                  {/* honor column kept from original page */}
                  {d?.customer && (d as any)?.customer?.createdByUser
                    ? `${(d as any)?.customer?.createdByUser?.firstName || ''} ${(d as any)?.customer?.createdByUser?.middleName || ''} ${(d as any)?.customer?.createdByUser?.lastName || ''}`
                        .trim() || (d as any)?.customer?.createdByUser?.name
                    : (d as any)?.customer?.createdByBusiness
                    ? `${(d as any)?.customer?.createdByBusiness?.firstName || ''} ${(d as any)?.customer?.createdByBusiness?.middleName || ''} ${(d as any)?.customer?.createdByBusiness?.lastName || ''}`
                        .trim() || (d as any)?.customer?.createdByBusiness?.name
                    : '-'}
                </TableCell>

                <TableCell sx={{ border: '1px solid #e5e7eb', fontWeight: 600 }}>
                  <Stack spacing={0.5}>
                    <Typography variant="body2" fontWeight={600}>
                      {formatCurrency(d?.loanSize)}
                    </Typography>
                    <Typography variant="body2">
                      {d.dealType
                        ? d.dealType.charAt(0).toUpperCase() + d.dealType.slice(1)
                        : '-'}
                    </Typography>
                  </Stack>
                </TableCell>

                <TableCell sx={{ border: '1px solid #e5e7eb' }}>
                  {/* Keep the assigned bankers chips as in original page by rebuilding simple cell */}
                  {d?.assignedBanks && d.assignedBanks.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {d.assignedBanks.map((pivot: any) => {
                        const banker = pivot.bank;
                        if (!banker) return null;

                        const initials = (banker as any).firstName || banker.name || (banker as any).first || 'B';
                        let color: 'primary' | 'success' | 'error' = 'primary';
                        if (pivot.interestStatus === dealsStatus.Approved) color = 'success';
                        else if (pivot.interestStatus === dealsStatus.Rejected) color = 'error';

                        return (
                          <Box key={(banker as any).id || pivot.bankId} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip
                              title={
                                `${(banker as any).firstName || ''} ${(banker as any).middleName || ''} ${(banker as any).lastName || ''}`
                                  .trim() || banker.name || 'Unknown Banker'
                              }
                            >
                              <Chip label={initials} size="small" color={color} variant="filled" sx={{ fontWeight: 'bold' }} />
                            </Tooltip>

                            {pivot.interestStatus === dealsStatus.Approved && (
                              <Tooltip title="View Term Sheet">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => onViewTermSheet(d, banker.id)}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Chip label="Unassigned" size="small" variant="outlined" color="default" />
                  )}
                </TableCell>

                <TableCell sx={{ border: '1px solid #e5e7eb' }}>{d.maxLTV}%</TableCell>
                <TableCell sx={{ border: '1px solid #e5e7eb' }}>{d.minDSCRatio}</TableCell>

                <TableCell sx={{ border: '1px solid #e5e7eb' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={d.status || '-'}
                      color={getStatusColor(d.status) as any}
                      variant="filled"
                      size="small"
                      onClick={actionsDisabled ? undefined : () => onClosingClick(d)}
                      sx={{ cursor: actionsDisabled ? 'default' : 'pointer' }}
                    />
                  </Box>
                </TableCell>

                <TableCell sx={{ border: '1px solid #e5e7eb' }}>
                  <Chip
                    label={formatClosingDate(d.closingDate)}
                    size="small"
                    variant={d.closingDate ? 'filled' : 'outlined'}
                    color={d.closingDate ? 'success' : 'default'}
                    onClick={actionsDisabled ? undefined : () => onClosingClick(d)}
                    sx={{ cursor: actionsDisabled ? 'default' : 'pointer', fontWeight: 700 }}
                  />
                </TableCell>

                <TableCell align="right" sx={{ border: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                  {loggedInUser && loggedInUser?.role !== ROLES.CUSTOMER && (
                    <Tooltip title={actionsDisabled ? "Actions disabled" : "Assign Bankers"}>
                      <span>
                      <IconButton onClick={() => onAssignClick(d)} size="small" color="primary" disabled={actionsDisabled}>
                        <GroupAddIcon fontSize="small" />
                      </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  <Tooltip title={actionsDisabled ? "Actions disabled" : "Edit"}>
                    <span>
                    <IconButton onClick={() => onEditClick(d)} size="small" color="secondary" disabled={actionsDisabled}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title={actionsDisabled ? "Actions disabled" : "Add Task"}>
                    <span>
                    <IconButton onClick={() => onAddTaskClick(d)} size="small" color="info" disabled={actionsDisabled}>
                      <AddTaskIcon fontSize="small" />
                    </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title={actionsDisabled ? "Actions disabled" : "Delete"}>
                    <span>
                    <IconButton onClick={() => onDeleteClick(d.id)} size="small" color="error" disabled={actionsDisabled}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns.length}>
                  <Collapse in={expandedRows.has(d.id!)} timeout="auto" unmountOnExit>
                    <Box sx={{ margin: 1 }}>
                      {d?.assignedBanks &&
                        d.assignedBanks.map((pivot: any) => {
                          const banker = pivot.bank;
                          if (!banker) return null;
                          return (
                            <BankerDocumentsView
                              key={banker.id}
                              dealId={d.id!}
                              bankerId={banker.id}
                              bankerName={`${banker.firstName || ''} ${banker.middleName || ''} ${banker.lastName || ''}`.trim() || banker.name}
                              onViewTermSheet={(bankerId) => onViewTermSheet(d, bankerId)}
                            />
                          );
                        })}
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DealsDesktopTableView;
